//@ts-check
import { useContext, useState, useEffect, useRef, useCallback, useMemo, createContext } from "react"
import useRawPromise from "../../hooks/useRawPromise"
import TimezoneContext from "../Timezone"
import TestCategoriesContext from "../TestCategories"
import { stripClub, stripTestCategory, stripAnalyses } from '../../utils/stripData'
import useUnit from "../../hooks/useUnit"
import LocaleContext from "../Locale"
import { useIntl } from "react-intl"
import ProfileContext from "../Profile"
import fetchPDF from '../../utils/fetchPDF'
import useRawState from "../../hooks/useRawState"
import { useSearchParams } from "react-router"
import { filterTestCategoriesOfAnalyses, getTotalProgress } from "../../utils/pdfGenerator"
import { format } from 'date-fns'
import dp from 'dompurify'
import useBridge from "../../hooks/useBridge"

/** @type {import('react').Context<any>} */
const MatchReportContext = createContext(undefined)
export default MatchReportContext

const SECTION_NAMES = {
    coverPage: 'coverPage',
    testCategories: 'testCategories',
    matchReport: 'matchReport',
    contactPage: 'contactPage',
}

const deviceMeasurementTypesToClean = [
    "energy-restitution",
    'hardness',
    "rotational-stiffness",
    "rotational-traction",
    "shock-absorption",
    "spring-rate",
    "stud-depth",
    "surface-deformation",
    "torque-10-deg",
]

const MAX_USER_SELECTED_METRICS = 12

// end date will be determined by the event.
// for tests, we will collect data from 2 months prior, and notify the user with a message,
// if there are fields with no data from the last 2 months prior to the 

export function MatchReportProvider({ children, ...props }) {
    const { get } = useRawPromise()
    const { convertToUnits, formatUnitName } = useUnit()
    const { formatMessage } = useIntl()
    const { datefnsLocale, locale } = useContext(LocaleContext)
    const { reporterTz } = useContext(TimezoneContext)
    const { data: profile } = useContext(ProfileContext)
    const hasPerformanceFeature = profile?.featureFlags?.includes('performance')

    //Match Events
    const [params] = useSearchParams()
    const prefillMatchId = params.get('matchEvent') ?? null
    const usePrefilledMatch = !!prefillMatchId

    // match picker in case no match is prefilled
    /** @type {[any, Function]} */
    const [selectedMatch, setSelectedMatch] = useState()  // option

    const {
        data: matches = [],
        firstPage,
        lastPage,
        isBusy: matchSelectBusy,
    } = useRawState(
        usePrefilledMatch ? undefined : `/api/current/match-events`,
        {
            page: 1,
            pagination: true,
            itemsPerPage: 100,
            "order[timestamp]": 'DESC'
        }
    )

    const matchOptions = matches
        .filter(m => !!m.pitch)
        .toSorted((a, b) => b.timestamp - a.timestamp)
        .map(m => ({
            value: m.id,
            label: m.pitch.name +
                ' - ' + format(new Date(m.timestamp * 1000), 'PP', { locale: datefnsLocale })
                + (m.opponent ? ` - ${m.opponent}` : '')
        }))

    const { data: matchData, isBusy: matchDataBusy } = useRawState(
        usePrefilledMatch ? `/api/current/match-events/${prefillMatchId}` :
            selectedMatch ? `/api/current/match-events/${selectedMatch.value}` : undefined
    )

    const { data: wateringTypes, loading: loadingWateringTypes } = useBridge(`/api/current/frontend-bridge/watering-types`)
    const { data: drillTypes, loading: waitingForDrillTypes } = useBridge(`/api/current/frontend-bridge/pitch-protection-drill-types`)
    const { data: bootTypes, loading: waitingForBootTypes } = useBridge(`/api/current/frontend-bridge/boot-types`)

    // TESTS
    const { data: testCategories = [], busy: testCategoriesBusy } = useContext(TestCategoriesContext)
    const [selectedTestCategories, setSelectedTestCategories] = useState([])

    // PITCH
    const { data: pitchData, isBusy: pitchBusy } = useRawState(
        (matchData?.pitch?.id) ? `/api/current/pitches/${matchData?.pitch?.id}` : undefined,
    )

    // INDEXES
    // if we don't have a sport, we can't fetch indexes.
    const { data: indexData = [], isBusy: indexesBusy } = useRawState(
        (pitchData && hasPerformanceFeature) ? `/api/current/index-categories` : undefined,
        { pagination: false }
    )

    const relevantIndexes = (pitchBusy ? [] : indexData)
        .filter(ind => ind.sport === pitchData?.club?.sport)
        .filter(ind => ind.indexCategoryType !== 'boot')

    const bootIndex = (pitchBusy ? [] : indexData)
        .filter(ind => ind.sport === pitchData?.club?.sport)
        .find(ind => ind.indexCategoryType === 'boot')

    //boot index is included through its own separate checkbox.
    const [showBootIndexCheckbox, setShowBootIndexCheckbox] = useState(false)
    const [includeBootIndex, setIncludeBootIndex] = useState(false)

    useEffect(() => {
        if (bootIndex) {
            setShowBootIndexCheckbox(true)
            setIncludeBootIndex(true)
        }
    }, [bootIndex])

    /** @type {[any, Function]} */
    const [selectedIndexes, setSelectedIndexes] = useState([])

    const [maxSelectedIndexes, maxSelectedCategories] = useMemo(() => {
        return [
            MAX_USER_SELECTED_METRICS - selectedTestCategories.length,
            MAX_USER_SELECTED_METRICS - selectedIndexes.length,
        ]
    }, [selectedTestCategories, selectedIndexes])

    // Note
    const [note, setNote] = useState('')

    useEffect(() => {
        if (!matchData || !matchData.pitchComments) return
        setNote(matchData.pitchComments)
    }, [matchData])

    // options
    const [reportTitle, setReportTitle] = useState(formatMessage({ id: 'matchReport' }))
    const [organisationName, setOrganisationName] = useState('')
    const [finalOpponentName, setFinalOpponentName] = useState('')

    const [includeCoverPage, setIncludeCoverPage] = useState(false)
    const [includeContactPage, setIncludeContactPage] = useState(false)

    const [printFriendly, setPrintFriendly] = useState(false)

    useEffect(() => {
        if (pitchData && pitchData.club?.name) {
            setOrganisationName(pitchData.club.name)
        }
    }, [pitchData])

    useEffect(() => {
        if (matchData?.opponent) {
            setFinalOpponentName(matchData?.opponent)
        }
    }, [matchData])

    // data
    /** @type {[any, Function]} */
    const [data, setData] = useState({})  //BIIIIG Data

    /** @type {[{ msg: string, progress: number } | undefined, Function]} */
    const [fetchingBusyState, setFetchingBusyState] = useState() // {msg, progress}
    const dataReady = useMemo(() => {
        return fetchingBusyState?.progress === 100
    }, [fetchingBusyState])

    // request abortion logic
    const allowFetchRef = useRef(true)
    const abortFetches = useCallback(() => {
        allowFetchRef.current = false
    }, [])
    const allowFetches = useCallback(() => {
        allowFetchRef.current = true
    }, [])
    useEffect(() => {
        return abortFetches
    }, [])


    const handleFetchAllData = useCallback(async () => {
        setData({})
        const progressWeights = [50, 50]

        //club, match, wateringTypes
        try {
            const club = pitchData?.club
            if (!club) {
                throw new Error('Pitch or club data is missing')
            }
            stripClub(club)

            const convertedGrass = convertToUnits(matchData.grassHeight, 'mm', { useSecondaryUnit: false })
            const hasWeather = matchData.weatherConditions?.averageTemperature !== undefined &&
                matchData.weatherConditions?.averageTemperature !== null
            const convertedWeather = {}
            if (hasWeather) {
                convertedWeather.averageTemperature = convertToUnits(matchData.weatherConditions.averageTemperature, '°C', { useSecondaryUnit: false })
                convertedWeather.totalRainfall = convertToUnits(matchData.weatherConditions.totalRainfall, 'mm', { useSecondaryUnit: false })
                convertedWeather.averageWindSpeed = convertToUnits(matchData.weatherConditions.averageWindSpeed, 'm/s', { useSecondaryUnit: false })
            }
            for (const [k, v] of Object.entries(convertedWeather)) {
                convertedWeather[k] = { value: v.primaryValue, unit: v.primaryUnit?.name }
            }

            setData({
                pitch: pitchData,
                match: {
                    ...matchData,
                    grassHeight: convertedGrass.primaryValue,
                    grassHeightUnit: convertedGrass.primaryUnit?.name,
                    hasWeather,
                    convertedWeather,
                },
                drillTypes,
                bootTypes,
                wateringTypes,
                bootIndex,
            })
        } catch (e) {
            console.log(e)
            setFetchingBusyState({
                msg: formatMessage({ id: 'couldNotRetrieveClubError' }),
                progress: 0
            })
            abortFetches()
            return setData({})
        }

        try {
            // FETCH TESTCATEGORIES
            const testCategoriesBuffer = []
            let catCount = 1
            for (const catId of selectedTestCategories) {
                setFetchingBusyState({
                    msg: formatMessage(
                        { id: 'fetchingCategoryXOfY', },
                        {
                            catCount,
                            catTotal: selectedTestCategories.length,
                        },
                    ),
                    progress: getTotalProgress(
                        [
                            catCount / selectedTestCategories.length * 100,
                            0,
                        ]
                        , progressWeights
                    )
                })
                catCount++
                if (!allowFetchRef.current) return
                const { data: category } = await get(`/api/current/test-categories/${catId}`)
                testCategoriesBuffer.push(category)
            }
            // mutate data to make lightweight and converted to units
            for (const cat of testCategoriesBuffer) {

                const doConvert = !!cat.unitSI

                stripTestCategory(cat)

                const convert = (val) => convertToUnits(val, cat.unitSI, { metricPrecision: cat.allowedDecimals, useSecondaryUnit: false })
                const conversionObj = convert(1)
                const convertedUnitName = conversionObj.converted ? conversionObj.primaryUnit?.name : cat.unitSI
                const convertedPrecision = conversionObj.converted ? conversionObj.primaryUnit?.convertedPrecision : cat.allowedDecimals

                cat.convertedUnit = convertedUnitName
                cat.convertedDecimals = doConvert ? convertedPrecision : cat.allowedDecimals

                if (doConvert) {
                    cat.maxRatingValue = convert(cat.maxRatingValue).primaryValue
                    cat.minRatingValue = convert(cat.minRatingValue).primaryValue
                    cat.ratings = cat.ratings.map(rating => {
                        return {
                            ...rating,
                            max: convert(rating.max).primaryValue,
                            min: convert(rating.min).primaryValue,
                        }
                    })
                }
            }

            setData(prev => ({ ...prev, testCategories: testCategoriesBuffer }))

            // INDEX CATEGORIES
            const indexCategoriesData = relevantIndexes.filter(ind => selectedIndexes.includes(ind.id))

            setData(prev => ({ ...prev, indexCategories: indexCategoriesData }))

            // FETCH ANALYSES
            let analysesBuffer = []

            setFetchingBusyState({
                msg: formatMessage({ id: 'collectingTests' }),
                progress: getTotalProgress([100, 0], progressWeights),
            })

            const pitchId = pitchData.id

            // derive start and end: in this case just 2 months up to the event.
            const eventTime = matchData.timestamp
            const eventTimeMinusTwoMonths = eventTime - 60 * 60 * 24 * 62

            if (!allowFetchRef.current) return
            const fetchIndexes = relevantIndexes.length > 0 || !!bootIndex
            const { data } = await get(
                `/api/current/pitches/${pitchId}/analyses${fetchIndexes ? '/index-results' : ''}?from=${eventTimeMinusTwoMonths}&to=${eventTime}&itemsPerPage=20`,
                { unpackHydra: false }
            )
            analysesBuffer.push(...data['hydra:member'])

            // fetch other pages if needed
            let currentData = data
            while (true) {
                const nextPage = currentData['hydra:view']?.['hydra:next']
                if (!nextPage) break
                if (!allowFetchRef.current) return
                const { data: nextData } = await get(nextPage, { unpackHydra: false })
                analysesBuffer.push(...nextData['hydra:member'])
                currentData = nextData
            }
            setFetchingBusyState({
                msg: formatMessage({ id: 'searchedXanalysesFromYofZPitches' }, {
                    analCount: analysesBuffer.length,
                    pitchCount: 1,
                    pitchTotal: 1,
                }),
                progress: 100,
            })

            filterTestCategoriesOfAnalyses(analysesBuffer, selectedTestCategories)
            stripAnalyses(analysesBuffer, { stripIndexes: false })
            // filter out analyses with no tests left
            analysesBuffer = analysesBuffer.filter(anal => (Array.isArray(anal.tests) && anal.tests.length > 0))
            // convert values to user's unit system
            analysesBuffer.forEach(anal => {
                anal.tests.forEach(test => {
                    const { allowedDecimals, unitSI, measurementType } = testCategoriesBuffer.find(cat => cat.id === test.testCategory)
                    test.zoneResults.forEach(zR => {
                        // quick dirty fix to clean zeros from digital devices. TO DO: firmware needs to avoid this
                        if (zR.val === 0 && deviceMeasurementTypesToClean.includes(measurementType)) zR.val = null

                        zR.val = convertToUnits(zR.val, unitSI, { metricPrecision: allowedDecimals, useSecondaryUnit: false }).primaryValue
                    })
                })
            })
            setData(prev => ({ ...prev, analyses: analysesBuffer }))
        } catch (e) {
            console.log(e)
            setFetchingBusyState({
                msg: formatMessage({ id: 'couldNotRetrieveAnalysesError' }),
                progress: 0
            })
            abortFetches()
            return setData({})
        }

        setFetchingBusyState({ progress: 100 })

    }, [pitchData, selectedTestCategories, selectedIndexes, relevantIndexes, matchData, wateringTypes, drillTypes, bootTypes, bootIndex])

    const retryFetchData = useCallback(() => {
        setData({})
        allowFetches()
        handleFetchAllData()
    }, [])

    const pdfDefaultState = {
        download: () => { },
        cleanup: () => { },
        busy: false,
        error: undefined,
    }

    /** @type {[any, Function]} */
    const [pdf, setPdf] = useState(pdfDefaultState)

    const submitReportGenerationRequest = useCallback(async () => {
        if (!data) return
        pdf.cleanup()
        setPdf({
            ...pdfDefaultState,
            busy: true,
        })

        const sectionsToExclude = [
            !includeCoverPage && SECTION_NAMES.coverPage,
            !includeContactPage && SECTION_NAMES.contactPage,
        ].filter(v => !!v)

        const sanitizedNote = dp.sanitize(note)

        const payloadJSON = {
            report: {
                reportType: 'matchReport',
                reportTitle,
                organisationName,
                includeLogo: true,
                logoSrc: data.pitch?.club?.logo?.file,
                sectionsToExclude,
                locale,
                datefnsLocale,
                reporterTz,
                printFriendly,
            },
            sanitizedNote,
            finalOpponentName,
            ...data
        }

        if (!includeBootIndex) {
            delete payloadJSON.bootIndex
        }

        try {
            const fileName = `${reportTitle}.pdf`

            const { a, revokeURL } = await fetchPDF(payloadJSON, fileName)

            setPdf({
                busy: false,
                download: () => a.click(),
                cleanup: () => {
                    revokeURL()
                    setPdf(pdfDefaultState)
                }
            })

        } catch (error) {
            console.error('Error generating pdf:', error)
            const err =
                typeof error === 'object' && error !== null && 'response' in error
                    ? error.response
                    : error
            setPdf({
                ...pdfDefaultState,
                error: err,
                busy: false,
            })
        }
    }, [
        matchData,
        note,
        data, pdf, datefnsLocale, locale,
        reportTitle, organisationName, includeCoverPage, finalOpponentName,
        includeBootIndex,
        includeContactPage, profile,
        reporterTz, printFriendly
    ])

    const isMatchDeprecated = !!matchData && !matchData.wateringType
    const firstStepValid = !matchSelectBusy && !pitchBusy && !indexesBusy && matchData?.wateringType && !loadingWateringTypes && wateringTypes && !waitingForDrillTypes && drillTypes && !waitingForBootTypes && bootTypes
    const secondStepValid = !testCategoriesBusy && !indexesBusy && (selectedTestCategories.length > 0 || selectedIndexes.length > 0)

    return (
        <MatchReportContext.Provider
            value={{

                testCategories, selectedTestCategories, setSelectedTestCategories,
                relevantIndexes, selectedIndexes, setSelectedIndexes,
                maxSelectedIndexes, maxSelectedCategories, MAX_USER_SELECTED_METRICS,
                allowFetches, abortFetches, handleFetchAllData, fetchingBusyState, dataReady, retryFetchData,
                firstStepValid,
                secondStepValid,
                reportTitle, setReportTitle, organisationName, setOrganisationName, finalOpponentName, setFinalOpponentName,
                includeCoverPage, setIncludeCoverPage,
                includeContactPage, setIncludeContactPage,
                data,
                submitReportGenerationRequest,
                pdf,
                matchOptions, selectedMatch, setSelectedMatch,
                hasPerformanceFeature,
                note, setNote,

                matchData, isMatchDeprecated,
                matchPickerBusy: matchSelectBusy || matchDataBusy,
                usePrefilledMatch,
                wateringTypes,
                printFriendly, setPrintFriendly,
                showBootIndexCheckbox, setShowBootIndexCheckbox,
                includeBootIndex, setIncludeBootIndex,
            }}
            {...props}
        >
            {children}
        </MatchReportContext.Provider>
    )
}